import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { Form, FormikProvider, useFormik } from 'formik';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';
// @mui
import {
  Box,
  Button,
  Container,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { LoadingButton } from '@mui/lab';
// libraries
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
// components
import { getUserTasksByUserIdAndProjectId } from '../../services/employeeService';
import { addManualTimeEntry, trackerUpdate } from '../../services/trackerService';
import Iconify from '../iconify';

TimeEntryModal.propTypes = {
  action: PropTypes.string.isRequired,
  taskName: PropTypes.string,
  projectId: PropTypes.number,
  startTime: PropTypes.any,
  stopTime: PropTypes.any,
  trackerId: PropTypes.number,
  fetchTimeline: PropTypes.any,
  projectList: PropTypes.array,
  employeeList: PropTypes.array,
  userId: PropTypes.any,
};

export default function TimeEntryModal({
  action,
  trackerId,
  taskId,
  projectId,
  startTime,
  stopTime,
  fetchTimeline,
  projectList,
  employeeList,
  userId,
}) {
  const [taskList, setTaskList] = useState([]);
  const [open, setOpen] = useState(null);
  const [fromTime, setFromTime] = useState(startTime ? dayjs(startTime) : dayjs(Date.now()));
  const [toTime, setToTime] = useState(stopTime ? dayjs(stopTime) : dayjs(Date.now()));
  const [diff, setDiff] = useState('');
  const [isSaving, setSaving] = useState(false);
  const [selectedUserId, setUserId] = useState(null);
  const [selectedProjectId, setProjectId] = useState(null);

  const getUserTasks = async () => {
    try {
      const userTasks = await getUserTasksByUserIdAndProjectId(selectedUserId, selectedProjectId);
      setTaskList(userTasks?.data?.data);
    } catch (error) {
      toast.error(error?.response?.data?.message ?? error.message);
    }
  };

  useEffect(() => {
    if (userId && projectId) {
      setProjectId(projectId);
      setUserId(userId);
    }
  }, [userId, projectId]);

  useEffect(() => {
    if (selectedProjectId && selectedUserId) {
      getUserTasks();
    }
  }, [selectedProjectId, selectedUserId]);

  const defaultFormData = useRef({
    taskId: taskId ?? '',
    projectId: projectId ?? '',
    userId: action === 'update' ? userId : '',
  });

  function msToTime(duration) {
    const milliseconds = duration;
    const seconds = Math.floor((milliseconds / 1000) % 60);
    const minutes = Math.floor((milliseconds / 1000 / 60) % 60);
    const hours = Math.floor((milliseconds / 1000 / 60 / 60) % 24);
    return `${hours}h ${minutes}m ${seconds}s`;
  }
  const getTimeDiff = () => {
    if (fromTime && toTime) {
      const dif = toTime?.$d?.getTime() - fromTime?.$d?.getTime();
      setDiff(msToTime(dif));
    } else {
      setDiff('');
    }
  };

  useEffect(() => {
    getTimeDiff();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toTime, fromTime]);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(null);
  };

  const addmanualDataschema = Yup.object({
    userId: action === 'create' && Yup.string().required('User Name is required'),
    taskId: Yup.number().required('Note is required'),
  });
  const formik = useFormik({
    initialValues: defaultFormData.current,
    validationSchema: addmanualDataschema,
    onSubmit: async (values) => {
      const curr = new Date().getTime();
      if (toTime?.$d?.getTime() >= curr || toTime?.$d?.getTime() - fromTime?.$d?.getTime() <= 0) {
        toast.error('Stop Time should be less than current time');
      }
      if (toTime?.$d?.getTime() - fromTime?.$d?.getTime() > 0 && toTime?.$d?.getTime() < curr)
        if (action === 'create') {
          handleAddManualEntry(values);
        } else if (action === 'update') {
          handleUpdate(values);
        }
    },
  });

  const { touched, errors, getFieldProps, setSubmitting } = formik;

  const handleAddManualEntry = async (values) => {
    try {
      setSaving(true);
      const manualEntry = await addManualTimeEntry({ ...values, startTime: fromTime, stopTime: toTime });
      setSaving(false);
      fetchTimeline();
      toast.success(manualEntry?.data?.message);
      handleClose();
    } catch (error) {
      toast.error(error?.response?.data?.message ?? error.message);
      setSubmitting(false);
      setSaving(false);
    }
  };
  const handleUpdate = async (values) => {
    try {
      setSaving(true);
      const response = await trackerUpdate(trackerId, { ...values, startTime: fromTime, stopTime: toTime });
      setSaving(false);
      fetchTimeline();
      toast.success(response?.data?.message);
      handleClose();
      setSubmitting(false);
    } catch (error) {
      console.log(error);
      toast.error(error?.response?.data?.message ?? error.message);
      setSaving(false);
    }
  };
  return (
    <>
      {action === 'create' ? (
        <Button startIcon={<Iconify icon={'ic:round-add'} />} onClick={handleOpen}>
          Add a Time Entry Manually
        </Button>
      ) : (
        <Iconify icon={'material-symbols:edit'} style={{ color: '#2065D1', cursor: 'pointer' }} onClick={handleOpen} />
      )}

      <Modal
        sx={{
          display: 'flex',
          justifyContent: 'center',
        }}
        open={open}
        onClose={handleClose}
      >
        <>
          <Box
            sx={{
              width: { xs: '90vw', sm: '70vw', md: '50vw' },
              zIndex: 2,
              display: 'flex',
              justifyContent: 'center',
              flexDirection: 'column',
              margin: 'auto',
            }}
          >
            <Paper
              sx={{
                boxShadow: '1px 1px 8px rgba(69,76,91,.2)',
                borderRadius: '12px',
                padding: '30px',
                width: { xs: '90vw', sm: '70vw', md: '50vw' },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1,
                }}
              >
                <Typography
                  variant="h5"
                  align="center"
                  sx={{
                    m: '8px 0',
                    color: '#999',
                    fontWeight: '600',
                    fontSize: { xs: '.9rem', sm: '1rem', md: '1.25rem' },
                  }}
                >
                  {action === 'create' ? 'Add a Time Entry' : action === 'update' ? 'Edit a Time Entry' : ''}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <IconButton color="primary" onClick={handleClose}>
                    <Iconify icon="ic:round-clear" />
                  </IconButton>
                </Box>
              </Box>
              <Divider sx={{ mb: 1 }} />
              <FormikProvider value={formik}>
                <Form>
                  <Container maxWidth="sm">
                    <Stack spacing={2}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <Grid Container display="flex">
                          <Grid xs={6}>
                            <DateTimePicker
                              label="From"
                              maxDate={dayjs()}
                              onChange={(date) => setFromTime(date)}
                              value={fromTime}
                              renderInput={(params) => <TextField {...params} />}
                            />
                          </Grid>
                          <Grid xs={6}>
                            <DateTimePicker
                              label="To"
                              minDateTime={fromTime}
                              maxDate={dayjs()}
                              value={toTime}
                              onChange={(date) => setToTime(date)}
                              renderInput={(params) => <TextField {...params} />}
                            />
                          </Grid>
                        </Grid>
                      </LocalizationProvider>
                      <TextField label="Duration" name="duration" disabled value={diff} fullWidth />

                      <FormControl sx={{ m: 1, minWidth: 120 }}>
                        <InputLabel htmlFor="select-employee">Employee</InputLabel>
                        <Select
                          id="userId"
                          label="Employee"
                          name="userId"
                          {...getFieldProps('userId')}
                          error={touched.userId && Boolean(errors.userId)}
                          helperText={touched.userId && errors.userId}
                          onChange={(e) => {
                            setUserId(e.target.value);
                            formik.setFieldValue('userId', e.target.value);
                          }}
                        >
                          {employeeList?.map((userId) => (
                            // eslint-disable-next-line react/jsx-key
                            <MenuItem key={userId.id} value={userId?.id}>
                              {userId?.userName}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl sx={{ m: 1, minWidth: 120 }}>
                        <InputLabel htmlFor="select-project">Project</InputLabel>
                        <Select
                          id="projectId"
                          label="Project"
                          name="projectId"
                          disabled={action === 'update'}
                          {...getFieldProps('projectId')}
                          error={touched.projectId && Boolean(errors.projectId)}
                          helperText={touched.projectId && errors.projectId}
                          onChange={(e) => {
                            setProjectId(e.target.value);
                            formik.setFieldValue('projectId', e.target.value);
                          }}
                        >
                          <MenuItem value={null}>No Project</MenuItem>
                          {projectList?.map((projectId) => (
                            // eslint-disable-next-line react/jsx-key
                            <MenuItem key={projectId.id} value={projectId?.id}>
                              {projectId?.projectName}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl sx={{ m: 1, minWidth: 120 }}>
                        <InputLabel htmlFor="select-task">Task Name</InputLabel>
                        <Select
                          id="taskId"
                          label="Task name"
                          name="taskId"
                          // onChange={handleChange}
                          {...getFieldProps('taskId')}
                          error={touched.taskId && Boolean(errors.taskId)}
                          helperText={touched.taskId && errors.taskId}
                        >
                          {taskList &&
                            taskList.map((task) => {
                              if (task.userId === formik.values.userId && task.projectId === formik.values.projectId) {
                                return (
                                  <MenuItem key={task.id} value={task.id}>
                                    {task.taskName}
                                  </MenuItem>
                                );
                              }
                              return null;
                            })}
                        </Select>
                      </FormControl>
                      <LoadingButton
                        fullWidth
                        size="large"
                        type="submit"
                        variant="contained"
                        loading={isSaving}
                        disabled={toTime?.$d?.getTime() - fromTime?.$d?.getTime() < 0}
                      >
                        Save
                      </LoadingButton>
                    </Stack>
                  </Container>
                </Form>
              </FormikProvider>
            </Paper>
          </Box>
        </>
      </Modal>
    </>
  );
}
